import {
  CityPricingChanges,
  CityPriceType,
  FareSimulationRequest,
  RegionalPricing,
  RegionalPricingChanges,
  RegionalPricingKey,
  TripType,
} from "../../shared/dtos/internationalPricingDTO";
import { OriginDTO } from "../../shared/dtos/originDTO";
import { DestinationDTO } from "../../shared/dtos/destinationsDTO";

export const REGION_OPTIONS: Array<{
  value: RegionalPricingKey;
  label: string;
}> = [
  { value: "north_america", label: "North America" },
  { value: "latin_america", label: "Latin America" },
  { value: "europe", label: "Europe" },
  { value: "asia", label: "Asia" },
  { value: "middle_east", label: "Middle East" },
  { value: "africa", label: "Africa" },
  { value: "oceania", label: "Oceania" },
  { value: "other", label: "Other" },
];

export const PRICE_TYPE_OPTIONS: Array<{
  value: CityPriceType;
  label: string;
}> = [
  { value: "automatic", label: "Automático (portal)" },
  { value: "manual", label: "Manual" },
];

export const normalizeSearchText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const regionalNumericFields: Array<keyof RegionalPricingChanges> = [
  "kilometerMinMultiplier",
  "kilometerMaxMultiplier",
  "fareMinMultiplier",
  "fareMaxMultiplier",
  "cityMultiplier",
  "minimumChargeMultiplier",
  "usdFloor",
];

export const getRegionalChanges = (
  current: RegionalPricing,
  original: RegionalPricing,
): RegionalPricingChanges =>
  regionalNumericFields.reduce<RegionalPricingChanges>((changes, field) => {
    if (current[field] !== original[field]) {
      changes[field] = current[field];
    }
    return changes;
  }, {});

export const getChangedFields = <T extends Record<string, unknown>>(
  current: T,
  original: T,
): Partial<T> =>
  Object.keys(current).reduce<Partial<T>>((changes, key) => {
    const field = key as keyof T;
    if (current[field] !== original[field]) {
      changes[field] = current[field];
    }
    return changes;
  }, {});

export const validateRegionalPricing = (
  row: RegionalPricing,
): string | undefined => {
  if (regionalNumericFields.some((field) => row[field] < 0)) {
    return "Los valores no pueden ser negativos.";
  }
  if (row.kilometerMinMultiplier > row.kilometerMaxMultiplier) {
    return "KM mín no puede superar KM máx.";
  }
  if (row.fareMinMultiplier > row.fareMaxMultiplier) {
    return "Tarifa mín no puede superar Tarifa máx.";
  }
  return undefined;
};

export const validateCityPricingRanges = (
  values: CityPricingChanges,
): string | undefined => {
  const numericValues = Object.values(values).filter(
    (value): value is number => typeof value === "number",
  );
  if (numericValues.some((value) => value < 0)) {
    return "Los valores no pueden ser negativos.";
  }
  if (
    values.usdPerKmMin != null &&
    values.usdPerKmMax != null &&
    values.usdPerKmMin > values.usdPerKmMax
  ) {
    return "USD/KM mín no puede superar USD/KM máx.";
  }
  if (
    values.usdPerHourMin != null &&
    values.usdPerHourMax != null &&
    values.usdPerHourMin > values.usdPerHourMax
  ) {
    return "USD/hora mín no puede superar USD/hora máx.";
  }
  if (
    values.minimumFareFloorUsd != null &&
    values.minimumFareCeilingUsd != null &&
    values.minimumFareFloorUsd > values.minimumFareCeilingUsd
  ) {
    return "La tarifa mínima (piso) no puede superar el techo.";
  }
  return undefined;
};

export const removeUndefined = <T extends Record<string, unknown>>(
  values: T,
): T =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined),
  ) as T;

export const buildSimulationRequest = (
  values: {
    adminId: number;
    serviceTypeId: number;
    estimatedKm: number;
    tripType: TripType;
    quantity: number;
  },
  origin: OriginDTO,
  destination: DestinationDTO,
): FareSimulationRequest => ({
  ...values,
  originLatitude: origin.originLatitude,
  originLongitude: origin.originLongitude,
  destinationLatitude: destination.destinationLatitude,
  destinationLongitude: destination.destinationLongitude,
});

const roundMoney = (value: number) => Math.round(value * 100) / 100;

export const calculateCityPricingFromAirport = (
  airportUsd: number | null | undefined,
  airportKm: number | null | undefined,
  region: RegionalPricing | undefined,
): CityPricingChanges => {
  if (
    !region ||
    airportUsd == null ||
    airportKm == null ||
    airportUsd < 0 ||
    airportKm <= 0
  ) {
    return {};
  }

  const basePricePerKm = airportUsd / airportKm;
  return {
    usdPerKmMin: roundMoney(
      basePricePerKm * region.kilometerMinMultiplier,
    ),
    usdPerKmMax: roundMoney(
      basePricePerKm * region.kilometerMaxMultiplier,
    ),
    minimumFareFloorUsd: roundMoney(
      airportUsd * region.fareMinMultiplier,
    ),
    minimumFareCeilingUsd: roundMoney(
      airportUsd * region.fareMaxMultiplier,
    ),
    cityMinimumCostUsd: roundMoney(airportUsd * region.cityMultiplier),
    minimumChargeUsd: roundMoney(
      Math.max(
        airportUsd * region.minimumChargeMultiplier,
        region.usdFloor,
      ),
    ),
  };
};
